import { pipe } from "fp";

import * as Data from "~/Data";
import * as Document from "~/Document";
import { Mutable } from "~/Mutable";
import { useSelector, State as GlobalState } from "~/State";

/**
 * `Active` represents the `Document` which is currently being
 * viewed.
 * */
export type Active = Document.Document | undefined;

export type When<A> = {
  /** No document is currently being viewed; `Active` hasn't been set. */
  readonly inactive: () => A;

  /** `Active` resolves to a full `Document` and is ready to use */
  readonly available: (active: Mutable<Document.Document>) => A;
};

export const equals = (a: Active) => (b: Active): boolean => {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return Document.equals(a)(b);
};

/** Use the `Active` given what to do for each case of `When` */
export const use = (): Active =>
  useSelector({ equals, select: ({ documents: { active } }) => active });

export namespace State {
  export type State = Active;

  export const initial = (): State => undefined;

  export const when = <A>(
    { documents: { active, store } }: Mutable<GlobalState>,
    { inactive, available }: When<A>
  ): A =>
    !active
      ? inactive()
      : pipe(
          store,
          Data.Store.Get.fromReference({
            id: active.id,
            missing: () => inactive(),
            found: (document) => available(document),
          })
        );
}
